/**
 * Supabase Auth on the main thread only (spec 9.7 step 1, 15.2): `autoRefreshToken` off,
 * the session persisted in localStorage under one key, no URL detection (there is no
 * OAuth). The SDK is loaded on first use so it stays out of the shell entry; the worker
 * never imports it (apps/web/biome.json). The client refreshes the access token only
 * through `accessToken()`, which the session calls when online and before a sync, and
 * `segarkan()`, after the server changed the account's claims (a new password, plan 5).
 * The account screens (plan 5) reach the same client through `supabaseKlien()`. A session
 * left stored for no one is forgotten at start without the SDK (`lupakanSesi`): out of storage
 * at once, revoked by plain requests that store nothing.
 *
 * A failed refresh or a vanished session never touches local data: `sesi_lokal` is the
 * identity the app trusts offline; the API, not GoTrue, decides wipes.
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import { permintaanCabut } from '../app/fragmenSesi'
import type { AuthProvider } from './session'

export type { AuthProvider, SignInResult } from './session'

// Legacy storage name from SIMU; kept so devices keep their data (pinned: test/nama-warisan.test.ts).
export const AUTH_STORAGE_KEY = 'simu:auth'

let clientPromise: Promise<SupabaseClient> | null = null

/** The project URL and publishable key of this build; throws when the build has none. */
export function konfigurasiSupabase(): { url: string; key: string } {
  const url: unknown = import.meta.env.VITE_SUPABASE_URL
  const key: unknown = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
  if (typeof url !== 'string' || url.length === 0 || typeof key !== 'string' || key.length === 0) {
    throw new Error('VITE_SUPABASE_URL dan VITE_SUPABASE_PUBLISHABLE_KEY belum diatur.')
  }
  return { url, key }
}

/**
 * Revokes the session an e-mail link carried (GoTrue's local logout of that one session,
 * `keepalive`, never awaited): one the launch found in the address and no page takes (the
 * e-mail change's), and the reset link's when its page is left unfinished or the tab closes.
 * supabase-js's own sign-out waits on its lock and would not leave a closing tab. Never throws.
 */
export function cabutSesiTautan(akses: string): void {
  try {
    const { url, key } = konfigurasiSupabase()
    const { alamat, init } = permintaanCabut(url, key, akses)
    void fetch(alamat, init).catch(() => undefined)
  } catch {
    // No Supabase in this build: nothing to revoke there.
  }
}

/**
 * supabase-js counts a session as expired this long before its `expires_at` (auth-js
 * EXPIRY_MARGIN_MS) and refreshes it first.
 */
const MARGIN_KEDALUWARSA_MS = 90_000

/**
 * What GoTrue's local logout needs of a stored session (supabase-js's JSON under
 * AUTH_STORAGE_KEY): its access token while it is still good, else its refresh token (the
 * logout refuses an expired token; the SDK's own sign-out refreshes it first too). Null when the
 * value holds neither.
 */
function sesiUntukDicabut(
  tersimpan: string,
  sekarang: number,
): { akses: string } | { segarkan: string } | null {
  let sesi: unknown
  try {
    sesi = JSON.parse(tersimpan)
  } catch {
    return null
  }
  if (typeof sesi !== 'object' || sesi === null) return null
  const {
    access_token: akses,
    refresh_token: segarkan,
    expires_at: sampai,
  } = sesi as Record<string, unknown>
  const masihBerlaku =
    typeof sampai !== 'number' || sampai * 1000 - sekarang > MARGIN_KEDALUWARSA_MS
  if (typeof akses === 'string' && akses !== '' && masihBerlaku) return { akses }
  if (typeof segarkan === 'string' && segarkan !== '') return { segarkan }
  return null
}

/** GoTrue's refresh of a session by its refresh token, as a plain request that stores nothing. */
function permintaanSegarkan(
  url: string,
  kunci: string,
  segarkan: string,
): { alamat: string; init: RequestInit } {
  return {
    alamat: `${url.replace(/\/+$/, '')}/auth/v1/token?grant_type=refresh_token`,
    init: {
      method: 'POST',
      headers: { apikey: kunci, 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: segarkan }),
    },
  }
}

/**
 * Revokes a session already forgotten on this device (`lupakanSesi`) without the SDK, never
 * awaited: GoTrue's local logout of that one session by its access token, or by the one a
 * refresh gives when it has expired. The refreshed session is never stored. Never throws.
 */
function cabutSesiTersimpan(tersimpan: string): void {
  const sesi = sesiUntukDicabut(tersimpan, Date.now())
  if (sesi === null) return
  if ('akses' in sesi) {
    cabutSesiTautan(sesi.akses)
    return
  }
  try {
    const { url, key } = konfigurasiSupabase()
    const { alamat, init } = permintaanSegarkan(url, key, sesi.segarkan)
    void fetch(alamat, init)
      .then((r) => (r.ok ? (r.json() as Promise<unknown>) : null))
      .then((body) => {
        const akses =
          typeof body === 'object' && body !== null
            ? (body as { access_token?: unknown }).access_token
            : undefined
        if (typeof akses === 'string' && akses !== '') cabutSesiTautan(akses)
      })
      .catch(() => undefined)
  } catch {
    // No Supabase in this build: nothing to revoke there.
  }
}

/** The app's one Supabase client (loaded on first use); the MFA screens share it. */
export function supabaseKlien(): Promise<SupabaseClient> {
  clientPromise ??= import('@supabase/supabase-js').then(({ createClient }) => {
    const { url, key } = konfigurasiSupabase()
    return createClient(url, key, {
      auth: {
        autoRefreshToken: false,
        persistSession: true,
        detectSessionInUrl: false,
        storageKey: AUTH_STORAGE_KEY,
      },
    })
  })
  return clientPromise
}

/** A failure of the connection or of the server, not of the credentials. */
export function isNetworkFailure(error: {
  name?: string | undefined
  status?: number | undefined
}): boolean {
  if (error.name === 'AuthRetryableFetchError') return true
  return error.status === undefined || error.status === 0 || error.status >= 500
}

export function createSupabaseAuth(): AuthProvider {
  return {
    async signIn(email, password) {
      let sb: SupabaseClient
      try {
        sb = await supabaseKlien()
      } catch {
        return { ok: false, alasan: 'offline' }
      }
      const { data, error } = await sb.auth.signInWithPassword({ email, password })
      if (error !== null || data.session === null) {
        return {
          ok: false,
          alasan: error !== null && isNetworkFailure(error) ? 'offline' : 'tidak_cocok',
        }
      }
      return {
        ok: true,
        accessToken: data.session.access_token,
        userId: data.session.user.id,
        // GoTrue lists the account's factors with the user (the key is left out when none).
        adaFaktor: (data.session.user.factors ?? []).some((f) => f.status === 'verified'),
      }
    },
    storedSession() {
      return typeof localStorage === 'undefined' ? null : localStorage.getItem(AUTH_STORAGE_KEY)
    },
    async restoreSession(stored) {
      try {
        await (await supabaseKlien()).auth.signOut({ scope: 'local' })
      } catch {
        // The refused session is forgotten here whatever the server answers.
      }
      // Storage is the SDK's source of truth: the next getSession() reads this one.
      if (stored === null) localStorage.removeItem(AUTH_STORAGE_KEY)
      else localStorage.setItem(AUTH_STORAGE_KEY, stored)
    },
    async email() {
      try {
        const sb = await supabaseKlien()
        const { data, error } = await sb.auth.getUser()
        return error !== null ? null : (data.user?.email ?? null)
      } catch {
        return null
      }
    },
    async segarkan() {
      try {
        const sb = await supabaseKlien()
        const { data, error } = await sb.auth.refreshSession()
        if (error !== null || data.session === null) return null
        return data.session.access_token
      } catch {
        return null
      }
    },
    async accessToken() {
      // Nothing stored means nothing to refresh; skip loading the SDK for nothing.
      if (typeof localStorage !== 'undefined' && localStorage.getItem(AUTH_STORAGE_KEY) === null) {
        return null
      }
      try {
        const sb = await supabaseKlien()
        const { data, error } = await sb.auth.getSession()
        if (error !== null || data.session === null) return null
        return data.session.access_token
      } catch {
        return null
      }
    },
    async signOutLocal() {
      try {
        if (typeof navigator === 'undefined' || navigator.onLine) {
          const sb = await supabaseKlien()
          await sb.auth.signOut({ scope: 'local' })
        }
      } catch {
        // The server may be unreachable; the stored session goes regardless.
      }
      if (typeof localStorage !== 'undefined') localStorage.removeItem(AUTH_STORAGE_KEY)
    },
    lupakanSesi() {
      if (typeof localStorage === 'undefined') return
      const tersimpan = localStorage.getItem(AUTH_STORAGE_KEY)
      if (tersimpan === null) return
      // Gone in this very call: storage is the SDK's source of truth, so a sign-in that starts
      // next stores its own session and nothing removes or revokes that one later.
      localStorage.removeItem(AUTH_STORAGE_KEY)
      cabutSesiTersimpan(tersimpan)
    },
  }
}
